import styled, { css } from 'styled-components';
import media from 'components/mixins/media';
import { transparentize } from 'utils/color';
import { getCustomScrollbars } from 'components/mixins/customScrollbars';
import { secondaryFont } from 'components/mixins/typography';
import { TOC } from 'constants/components';
import { MEDIA_SMALL } from 'constants/screenResolutions';

export const BLOCK_MARGIN_OFFSET = 80;

type CourseLogoProps = {
  altText: string;
  logoUrl: string;
};

export const CourseLogo = styled.img.attrs((props: CourseLogoProps) => ({
  alt: props.altText,
  src: props.logoUrl
}))<CourseLogoProps>`
  padding-top: 90px;
  max-width: 200px;
  margin-bottom: 0;
`;

type StickyContainerProps = {
  background: any;
};

type QuestionBlockProps = {
  isFirstElement: boolean;
};

type ProgressProps = {
  progress: number;
};

type StickyHeadProps = {
  isExpanded: boolean;
};

export const StickyContainer = styled.div.attrs({
  'data-test': 'sticky-container'
})<StickyContainerProps>`
  ${props =>
    !props.background &&
    css`
      background: white;
    `};
  overflow-x: hidden;
  width: 100%;
  height: 100%;
  overflow-y: scroll !important;
  position: relative;
  ${getCustomScrollbars()};
`;

export const StickyHead = styled.div.attrs({
  'data-test': 'sticky-header'
})<StickyHeadProps>`
  background: white;
  z-index: 11;
  transition: left 1s linear;
  position: fixed;
  width: ${props => (props.isExpanded ? `calc(100% - ${TOC.EXPANDED_WIDTH}px)` : '100%')};

  @media (max-width: ${MEDIA_SMALL}px) {
    left: 0;
    top: 0;
    padding: 0 30px;

    > div:first-of-type {
      padding: 0 0 0 15px;
    }
  }
`;

export const StickyBody = styled.div.attrs(() => ({
  'data-test': 'sticky-body'
}))`
  width: 100%;
  font-size: 15px;
  margin: 0 auto;
  padding: 0 10%;
  ${media.small`
    padding: 0;
  `};
`;

export const SectionTitle = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 10%;
  ${media.small`
    padding: 0;
  `};
`;

export const SectionNumber = styled.div.attrs(() => ({
  'data-test': 'section-number'
}))`
  font-weight: bold;
  font-size: 12px;
  font-family: Qanelas-Soft, PT Serif, Serif;
  color: ${props =>
    transparentize(props.theme.colors.textColor, props.theme.themeUtils.getOpacity(true, 0.5))};
  line-height: 1em;
  padding: 15px 0 7px;
`;

export const SectionName = styled.div.attrs(() => ({
  'data-test': 'section-name'
}))`
  font-weight: bold;
  font-size: 18px;
  font-family: Qanelas-Soft, PT Serif, Serif;
  color: ${props => transparentize(props.theme.colors.textColor, 1)};
  line-height: 1.56em;
  padding: 0 0 12px;
`;

export const ProgressContainer = styled.div.attrs({
  'data-test': 'progress-bar-container'
})`
  width: 100%;
  background: ${props => transparentize(props.theme.colors.textColor, 0.08)};
`;

export const Progress = styled.div.attrs({
  'data-test': 'progress-bar'
})<ProgressProps>`
  height: 2px;
  background: ${props => transparentize(props.theme.colors.mainColor, 1)};
  width: ${props => (props.progress ? props.progress : 0)}%;
`;

export const LearningObjectiveBlock = styled.div`
  width: 100%;
  font-size: 15px;
  margin: 20px 0 31px 0;
`;

export const QuestionBlock = styled.div<QuestionBlockProps>`
  width: 100%;
  font-size: 15px;
  padding-top: ${props => (props.isFirstElement ? `90px` : `0px`)};
  margin: 20px 0 ${BLOCK_MARGIN_OFFSET}px 0;
`;

export const SectionSeparator = styled.div.attrs({
  'data-test': 'section-separator'
})`
  margin: 80px 0 32px;
`;

export const SectionCounter = styled.div`
  ${secondaryFont};
  color: ${props =>
    transparentize(props.theme.colors.textColor, props.theme.themeUtils.getOpacity(true, 0.5))};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;

  ${media.mobile`
    font-size: 12px;
  `}
`;

export const SectionHeader = styled.div`
  ${secondaryFont};
  color: ${props =>
    transparentize(props.theme.colors.textColor, props.theme.themeUtils.getOpacity(true, 0.5))};
  font-size: 32px;
  margin: 15px 0 0;
  font-weight: 900;
  line-height: 44.8px;
  ${media.medium`
    padding-right: 22px;
  `}
`;
